/**
 * PHA-1199: CCM bills only with 2+ qualifying chronic conditions. A clinic that does not
 * offer APCM (e.g. ODA) has no Tier 1 to fall back to, so a care plan covering fewer than
 * two can never be billed. The match endpoint reports the clinic's minimum
 * (`min_qualifying_conditions`, 0 = no minimum) and flags each matched condition
 * `qualifying`; the server enforces the same rule on generate and approve.
 */

import type { ErrorDetails } from '../components/ErrorModal';

export const CCM_ELIGIBILITY_TITLE = 'CCM Eligibility Requirements Not Met';
export const CCM_ELIGIBILITY_BODY =
  'CCM requires at least two qualifying chronic conditions. This patient currently has fewer than two supported conditions identified.';
export const CCM_ELIGIBILITY_ACTION = "Please review the patient's conditions before proceeding.";

/** PHA-1210: the eligibility refusal as a notice for the shared error modal. */
export const CCM_ELIGIBILITY_NOTICE: ErrorDetails = {
  kind: 'notice',
  title: CCM_ELIGIBILITY_TITLE,
  error: `${CCM_ELIGIBILITY_BODY}\n\n${CCM_ELIGIBILITY_ACTION}`,
};

export const APPROVAL_REFUSED_TITLE = "This care plan can't be approved yet";

/**
 * PHA-1210: the approve endpoint answers 400 with a plain-English reason when a business
 * rule refuses the plan (the CCM two-condition minimum, a PCMH clinic's missing Voice-of-
 * the-Patient goal, ...). That is guidance for the clinician, not a server fault, so it is
 * shown as a notice — the CCM refusal under its own title — instead of the "Server Error"
 * dialog with HTTP status, endpoint and "contact support". Anything else (5xx, auth, a
 * non-JSON body) is returned unchanged and keeps the error dialog.
 */
export function approvalRefusalNotice(errorData: ErrorDetails): ErrorDetails {
  if (errorData?.status !== 400 || !errorData.error) return errorData;
  if (errorData.error.startsWith(CCM_ELIGIBILITY_TITLE)) return CCM_ELIGIBILITY_NOTICE;
  return { kind: 'notice', title: APPROVAL_REFUSED_TITLE, error: errorData.error };
}

export interface QualifyingMatchOption {
  display_name: string;
  /** Absent from an older backend — treated as qualifying so the UI never over-blocks. */
  qualifying?: boolean;
  /** PHA-1207: this match justifies RPM (e.g. Hypertension). Only sent for an RPM patient. */
  rpm_justifying?: boolean;
}

/**
 * Why Generate is blocked, if it is:
 *  - 'chart': the chart itself has fewer qualifying conditions than the minimum — show the
 *    eligibility alert; nothing the clinician ticks can fix it.
 *  - 'selection': enough qualify, but fewer than the minimum are ticked.
 *  - null: not blocked.
 */
export type CcmGenerateBlock = 'chart' | 'selection' | null;

export function getCcmGenerateBlock(
  matchedDiseases: QualifyingMatchOption[],
  selectedDiseases: Set<string>,
  minQualifyingConditions: number,
  rpmOneDxAllowed = false
): CcmGenerateBlock {
  if (!minQualifyingConditions || minQualifyingConditions <= 0) return null;
  const qualifying = matchedDiseases.filter((d) => d.qualifying !== false);
  // PHA-1207: an RPM patient (the backend says so per patient) is satisfied by ONE ticked
  // RPM-justifying condition instead of the full minimum; two ticked qualifying still pass.
  const rpmJustifying = rpmOneDxAllowed ? qualifying.filter((d) => d.rpm_justifying === true) : [];
  if (qualifying.length < minQualifyingConditions && rpmJustifying.length === 0) return 'chart';
  const selected = qualifying.filter((d) => selectedDiseases.has(d.display_name)).length;
  if (selected >= minQualifyingConditions) return null;
  if (rpmJustifying.some((d) => selectedDiseases.has(d.display_name))) return null;
  return 'selection';
}
